/**
 * English messages of the built-in extra.json effects (W2-01b; formats-and-packaging.md P§5.2, DESIGN.md D24):
 * effect names and descriptions, uniform labels, and the "approximate" badge shown wherever a built-in effect is
 * previewed (it is reimplemented from Phira's behaviour, not Phira's shader). W3-04 added the fidelity labels of the
 * Phira-only content list (effects/summary.ts: effects and videos in Publish). Render code returns MessageRefs built
 * from these keys; the UI translates them.
 */

import { catalogOf, defineMessages } from '../../../../i18n/define';

export const m = defineMessages('builtinFx', {
  approximate: 'Approximate',
  approximateHint:
    "Phira's built-in effect, redrawn by Phiedit from its description. It can look slightly different in Phira.",

  // Phira-only content (extra.json effects and videos) and how faithfully the preview shows it.
  verified: 'Verified',
  verifiedHint: "Phira's built-in effect, redrawn by Phiedit and checked against Phira screenshots.",
  asWritten: 'As written',
  asWrittenHint: 'A shader from the package. The preview runs it as written, like Phira.',
  notPreviewed: 'Not previewed',
  notPreviewedHint: 'Phira shows it, but the preview does not. It is kept in the export.',
  videoApproximateHint:
    'The preview plays the video, but its timing and placement have not been checked against Phira yet. It is kept in the export.',
  videoNotPreviewedHint:
    'Phira plays the video, but the preview does not show it in this version. It is kept in the export.',
  customShader: 'Custom shader {path}',
  video: 'Video {path}',
  missingFile: '(missing file)',
  countApproximateEffects:
    '{count, plural, one {# built-in effect is approximate} other {# built-in effects are approximate}}',
  countNotPreviewedEffects: '{count, plural, one {# effect is not previewed} other {# effects are not previewed}}',
  countApproximateVideos:
    '{count, plural, one {# video is previewed approximately} other {# videos are previewed approximately}}',
  countNotPreviewedVideos: '{count, plural, one {# video is not previewed} other {# videos are not previewed}}',

  chromatic: 'Chromatic aberration',
  chromaticDesc: 'Splits red and blue apart, outward from the centre of the screen.',
  chromaticSampleCount: 'Samples',
  chromaticPower: 'Strength',

  circleBlur: 'Circle blur',
  circleBlurDesc: 'Spreads the brightest colour within a circle around every pixel: a soft, glowing blur.',
  circleBlurSize: 'Radius (px)',

  fisheye: 'Fisheye',
  fisheyeDesc: 'Magnifies the centre like a fisheye lens (positive strength) or pinches it (negative strength).',
  fisheyePower: 'Strength',

  glitch: 'Glitch',
  glitchDesc:
    'Shifts random horizontal bands sideways with a colour split; the pattern changes several times a second.',
  glitchPower: 'Shift',
  glitchRate: 'Amount',
  glitchSpeed: 'Changes per second',
  glitchBlockCount: 'Bands',
  glitchColorRate: 'Colour split',

  grayscale: 'Grayscale',
  grayscaleDesc: 'Fades the colours toward grey.',
  grayscaleFactor: 'Amount',

  noise: 'Noise',
  noiseDesc: 'Moves every pixel by a small random amount.',
  noiseSeed: 'Seed',
  noisePower: 'Strength',

  pixel: 'Pixelate',
  pixelDesc: 'Turns the picture into square blocks.',
  pixelSize: 'Block size (px)',

  radialBlur: 'Radial blur',
  radialBlurDesc: 'Blurs toward a centre point, like a fast zoom.',
  radialBlurCenterX: 'Centre X',
  radialBlurCenterY: 'Centre Y',
  radialBlurPower: 'Strength',
  radialBlurSampleCount: 'Samples',

  shockwave: 'Shockwave',
  shockwaveDesc: 'A ring that bends the picture as it expands. Animate Progress from 0 to 1.',
  shockwaveProgress: 'Progress',
  shockwaveCenterX: 'Centre X',
  shockwaveCenterY: 'Centre Y',
  shockwaveWidth: 'Ring width',
  shockwaveDistortion: 'Distortion',
  shockwaveExpand: 'Falloff',

  vignette: 'Vignette',
  vignetteDesc: 'Fades the edges of the screen toward a colour.',
  vignetteColor: 'Colour',
  vignetteExtend: 'Spread',
  vignetteRadius: 'Clear size',
});

export default catalogOf(m);
